// Display helpers for numbers, dates, assets, account paths, party titles.
//
// An amount shows at most 2 decimals for a coin and none for fiat, grouped in
// thousands (HOUSE-STYLE rule 10; full precision on the raw API). Dates and times are DUBAI TIME, on
// every screen, whatever clock the viewer's device keeps — see `booksClock`.
//
// SCOPE (docs/NAMING-AND-RELEVANCE.md §4): this file owns numbers, dates and
// assets. It does NOT grow a naming section — names and relevance live in
// `./naming.ts`, which every screen imports instead. The name-shaped helpers
// still exported below (`smartCase`, `railLabel`, `railLabelForAsset`,
// `prettyTreasurySlug`, `tidySlug`, `shortAccount`) predate that split and are
// kept working because screens still import them; they are marked
// `@deprecated` and are now primitives that `naming.ts` composes, not things
// a screen should call directly. Behavior is unchanged — nothing here was
// re-pointed, so no existing caller shifts.

// Operator ruling 2026-08-15: "max 2 decimal for USDT, no decimal for AED/INR
// accounts, for all party balances and all trade screens."
//
// DISPLAY ONLY. Storage is untouched — AED/INR are still integer minor units
// (2dp) and USDT is still 6dp end to end. Rounding what is STORED would break
// `treasury == physical`, which is the invariant the whole ledger rests on.
// This file is the one place the two diverge, on purpose.
//
// NOT changed by this rule, deliberately:
//   · RATES keep their precision. A cost basis of 100.3107 shown as 100.31 is a
//     different number, and margin is computed against it.
//   · INPUT fields keep theirs. Rounding what the operator is typing loses the
//     entry, rather than just displaying it more simply.
// AT MOST, NOT EXACTLY — HOUSE-STYLE v1.5 rule 10 (operator, 29–30 Oct 2026: "Currency- no decimals … crypto 2
// decimals, max 4-5 decimals", "No, BTC 4-5 decimals"; and "screens too", N-084). A coin shows at most two places,
// BTC five; fiat none. Rounded half up on the figure the BFF sent (a decimal string — never through a float, so
// 1.005 is 1.01), trailing zeros dropped: 19,125.11 USDT, 0.02564 BTC, 50,200 USDT, 120,001 AED. This replaces the
// 21 Sept rule (coins up to four, fiat two). A caller that RECONCILES — the cheque screens to the fils, a statement's tie-out, a deal's open
// box at the ledger's own scale — passes the places it reconciles at, and those stand: the exact figure is where the
// reconciling is. Rates are not amounts (fmtRate keeps their precision); neither is a box being typed in (shownAmount).
const FIAT_DECIMALS: Record<string, number> = { AED: 0, INR: 0, USD: 0, EUR: 0, GBP: 0 };
const CRYPTO_DECIMALS_DEFAULT = 2;
// A coin priced in the tens of thousands keeps five (16 Sept: at two places 0.02563553 BTC read 0.03, and v1.5 kept it).
const COIN_DECIMALS: Record<string, number> = { BTC: 5 };

/** Human-readable asset label. `USDTTRC` → `USDT·TRC`, fiat stays plain.
 *  Legacy codes (USDTTRC20/ERC20/BEP20 from pre-rename trades) collapse to
 *  the same display label as the canonical short forms — `displayAsset()`
 *  is the authority on UI grouping until a data migration retires them.
 *
 *  EVERY stablecoin, not just USDT (fixed 2026-08-27). This tested
 *  `startsWith('USDT')`, so `USDCERC` fell through and rendered as an unspaced
 *  blob beside a properly-formatted `USDT·ERC` — the same chain, two spellings,
 *  in one column. Operator caught it on the hot-erc wallet, which holds 70,000
 *  USDC-ERC and was the most visible case. */
const STABLE_PREFIX = ['USDT', 'USDC'];
export function displayAsset(code: string | undefined | null): string {
  if (!code) return '—';
  for (const p of STABLE_PREFIX) {
    if (code.startsWith(p) && code.length > p.length) {
      // Strip trailing "20" (legacy USDTTRC20 → TRC; USDCERC20 → ERC)
      const tail = code.slice(p.length).replace(/20$/, '');
      return `${p}·${tail}`;
    }
  }
  return code;
}

/**
 * Sum a per-asset-code totals map down to one row per displayAsset() label.
 * Used to fold legacy USDTTRC20 + canonical USDTTRC into a single "USDT·TRC"
 * row on Dashboard / Treasury views.
 */
export function collapseTotals(totals: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [code, v] of Object.entries(totals)) {
    const k = displayAsset(code);
    out[k] = (out[k] ?? 0) + v;
  }
  return out;
}

/** A figure as a plain decimal string: the BFF's own string when it is one (no float on the way), else the number to
 *  twelve places — enough that a float's tail (1.00499999…) reads as the figure meant (1.005). */
function decimalOf(value: string | number, n: number): string {
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim())) return value.trim();
  return n.toFixed(12);
}

/** Half up (away from zero, the sign kept) at `dp` places, on the decimal string itself. "1.005", 2 → "1.01". */
function roundHalfUp(dec: string, dp: number): string {
  const neg = dec.startsWith('-');
  const [whole, frac = ''] = (neg ? dec.slice(1) : dec).split('.');
  const digits = (whole! + frac.padEnd(dp + 1, '0').slice(0, dp)).replace(/^0+(?=\d)/, '');
  let n = BigInt(digits);
  if ((frac[dp] ?? '0') >= '5') n += 1n;
  const t = n.toString().padStart(dp + 1, '0');
  const out = dp === 0 ? t : `${t.slice(0, -dp)}.${t.slice(-dp)}`;
  return neg && /[1-9]/.test(out) ? `-${out}` : out;
}

export function fmtAmount(value: string | number | null | undefined, asset?: string, decimalsOverride?: number): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  const fiat = !!asset && FIAT_DECIMALS[asset] !== undefined;
  const decimals = decimalsOverride ?? (fiat ? FIAT_DECIMALS[asset!]! : COIN_DECIMALS[asset?.toUpperCase() ?? ''] ?? CRYPTO_DECIMALS_DEFAULT);
  const rounded = roundHalfUp(decimalOf(value, n), decimals);

  // A figure that is NOT zero must never render as a bare "0" — "settled" is the most consequential thing a balance
  // says, and it decides whether the operator chases a counterparty. Rule 10: it reads "under 1" (AED) or "under 0.01"
  // (a coin) — the finest place shown; fmtMoney adds the unit. Exactly "0" means exactly zero, everywhere. A negative
  // keeps its sign, "-under 1", as every negative figure here does: a small loss never reads as a small gain.
  if (n !== 0 && !/[1-9]/.test(rounded)) return `${n < 0 ? '-' : ''}under ${decimals === 0 ? '1' : `0.${'0'.repeat(decimals - 1)}1`}`;

  // PINNED LOCALE (operator, 1 Sept; 8 Oct 2026: "Universal numbering format not indian, everywhere"): international
  // grouping — 1,008,212 — never the viewer's OS default. Trailing zeros dropped — except that a fiat figure shown
  // with places (a reconciling caller's override) is whole or carries BOTH fils, never one: 2,343,431.10, not
  // 2,343,431.1 (21 Oct 2026, the cheque screens).
  const [whole, frac = ''] = rounded.replace(/^-/, '').split('.');
  let places = frac.replace(/0+$/, '');
  if (fiat && places !== '' && places.length < Math.min(2, decimals)) places = places.padEnd(Math.min(2, decimals), '0');
  const grouped = whole!.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${rounded.startsWith('-') ? '-' : ''}${grouped}${places ? `.${places}` : ''}`;
}

/** DUST: a venue's left-over folded on the Exchanges screens, never dropped — what the 21 Sept fold folded: under half
 *  the old finest place, 0.00005 of a coin, 0.000005 of a BTC. Not the same as reading "under" the places shown now
 *  (0.004 USDT reads "under 0.01" and is a holding); rule 10 changed how a figure reads, not what folds. */
const DUST_BELOW: Record<string, number> = { BTC: 0.000005 };
export function isDustFigure(value: string | number, asset: string): boolean {
  const n = Number(value);
  return Number.isFinite(n) && n !== 0 && Math.abs(n) < (DUST_BELOW[asset.toUpperCase()] ?? 0.00005);
}

/** WHAT A BOX THE CALCULATOR FILLED SHOWS (21 Oct 2026 audit): grouped in
 *  thousands — "27,247.9564" — where it showed the calculator's raw string,
 *  "27247.956403". DISPLAY ONLY: the raw string is still what the posting
 *  carries (nothing is rounded on its way to the books). A box is not a figure
 *  on a screen (rule 10 is): it keeps a field's places — four for a coin, the
 *  fils for fiat — so a box never hides what it is about to post. A box the
 *  operator typed in, and a box with the cursor in it, show the raw string — a
 *  formatter that reads "36,720" back as the European decimal 36.72 must never
 *  sit under an edit. A figure below the places shown stays raw too: a box
 *  never reads "under 0.0001". Shared by Between parties and the cheque deal
 *  page (Astra, 21 Oct). */
const BOX_DECIMALS_COIN = 4, BOX_DECIMALS_FIAT = 2;   // BTC keeps its five in a box too
export function shownAmount(raw: string, asset: string, derived: boolean, editing: boolean): string {
  const n = Number(raw);
  if (!derived || editing || raw.trim() === '' || !Number.isFinite(n)) return raw;
  const grouped = fmtAmount(n, asset, FIAT_DECIMALS[asset] !== undefined ? BOX_DECIMALS_FIAT : Math.max(BOX_DECIMALS_COIN, COIN_DECIMALS[asset.toUpperCase()] ?? 0));
  return /^-?under /.test(grouped) ? raw : grouped;
}

/**
 * The EXACT held quantity — for the one place where the exact reading IS the
 * point: the drill-down behind a rolled-up wallet.
 *
 * `fmtAmount` shows a coin at up to two places by the operator's own ruling,
 * and that is right for a balance column. It is wrong for a drill-down, which exists
 * precisely to say what the device holds: 9.90 BTC is not 9.90343 BTC, and a
 * roll-up that collapses six coins into one estimate has to be able to prove
 * itself against the real numbers when the operator opens it.
 *
 * Not a second number format. It is `fmtAmount` with the decimals set to what
 * the figure actually carries, through the same `decimalsOverride` the cheque
 * screen already uses to reconcile to the fils — so the locale, the grouping
 * and the "under" rule are all still the ones above. Trailing zeros are trimmed,
 * so 1,52,082 TRX does not read as 1,52,082.00000000.
 */
const MAX_QUANTITY_DECIMALS = 8;   // the finest scale admin.asset_registry holds
export function fmtQuantity(value: string | number | null | undefined, asset?: string): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  const fixed = Math.abs(n).toFixed(MAX_QUANTITY_DECIMALS).replace(/0+$/, '');
  const decimals = Math.max(0, fixed.length - fixed.indexOf('.') - 1);
  return fmtAmount(n, asset, Math.min(MAX_QUANTITY_DECIMALS, decimals));
}

/** `fmtAmount` + asset suffix in one go, e.g. "528,138 AED", "under 0.01 USDT·TRC". */
export function fmtMoney(value: string | number | null | undefined, asset?: string): string {
  if (value === null || value === undefined) return '—';
  return `${fmtAmount(value, asset)} ${displayAsset(asset)}`;
}

/**
 * THE ONE RATE FORMAT (item 70 of SWEEP-SCOPE-2026-09-13).
 *
 * A rate is not money and must never go through `fmtAmount`: 3.6725 rounded
 * to two places is 3.67, and on a pegged currency the whole margin lives in
 * the digits that rounding removes. There were four helpers with four
 * precisions — 3.67 printed "3.67" on Inventory, "3.6700" on Trading, "3.67"
 * on the Sheet at 4dp and "3.67" on Book trade at 8dp trimmed — so one figure
 * read three ways across the console.
 *
 * NO thousands grouping — a rate is a ratio, not a sum, and "60000" USDT
 * per BTC is read as one number, not as an amount. Absent or not a number is
 * an em dash, as `fmtAmount` does. Strings from the BFF are accepted so a
 * report need not `Number()` before printing.
 *
 * HOW MANY PLACES (operator, 21 Sept 2026: "decimals need to be corrected
 * across the book"; the last word, with the Trades log in hand: "rate
 * should be up to 4 decimals" — "4 decimal for AED & USD and EUR, 2 for
 * INR"). A rate the desk derives — a median, an average, a range, a rate
 * worked back from two amounts — carried float noise to eight places
 * (8422.30963871, 3.6730001). A rate is READ at the places its quote
 * currency is dealt in: AED, USD and EUR up to four (3.6725, 8422.3096),
 * INR two (100.80); a stablecoin or unknown quote four. THE FLOOR: a rate
 * under 1 keeps at least four significant digits, so a cheap coin's price
 * is not rounded away (0.19918 → 0.1992, never 0.20). Trailing zeros
 * trimmed — "up to": a rate typed short reads as typed (3.67 stays 3.67).
 *
 * DISPLAY ONLY. The stored rate is NUMERIC(36,8) and the engine measures
 * margin from the exact amounts, so nothing shortened here may reach the
 * books: an entry field, a rate a form derives and posts, the deal panel's
 * figure and any arithmetic on a rate go through `fmtRateExact` — the
 * stored figure, up to eight places, trailing zeros trimmed.
 */
/** The places a rate is read at, by its quote currency. */
const RATE_DECIMALS: Record<string, number> = { AED: 4, USD: 4, EUR: 4, GBP: 4, INR: 2, USDT: 4, USDC: 4 };
const RATE_DECIMALS_DEFAULT = 4;

function trimRate(fixed: string): string {
  return fixed.includes('.') ? fixed.replace(/0+$/, '').replace(/\.$/, '') : fixed;
}

/** International thousands grouping for an already-trimmed decimal string — 247310.2 → 247,310.2. The places are
 *  untouched; only the whole part is grouped. DISPLAY ONLY, as fmtRate is (the exact one stays bare). */
function groupWhole(s: string): string {
  const [whole, frac] = s.split('.');
  const grouped = whole!.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return frac === undefined ? grouped : `${grouped}.${frac}`;
}

/** The stored figure: up to eight places, trailing zeros trimmed. For what
 *  feeds the books — a form's derived rate, the deal panel, arithmetic. */
export function fmtRateExact(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  return trimRate(n.toFixed(8));
}

/** The rate as it is read: the quote currency's places, the floor under 1, grouped in thousands like every other
 *  figure on the desk (21 Oct 2026: a BTC cost read "247310.2" beside "1,008,212.50"; Astra).
 *  `quote` is the currency the rate is quoted in — "AED" of an AED-per-USDT
 *  rate; a display code with a chain ("USDT·TRC") is read by its first word. */
export function fmtRate(value: string | number | null | undefined, quote?: string | null): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  const code = /^[A-Za-z]+/.exec(quote ?? '')?.[0]?.toUpperCase() ?? '';
  let dp = RATE_DECIMALS[code] ?? RATE_DECIMALS_DEFAULT;
  if (n !== 0 && Math.abs(n) < 1) {
    // Four significant digits: the zeros after the point, then four more.
    const lead = Math.ceil(-Math.log10(Math.abs(n)));
    dp = Math.max(dp, Math.min(8, lead + 3));
  }
  return groupWhole(trimRate(n.toFixed(dp)));
}

/** A rate exactly as the server sends it (a decimal string): only its whole part grouped, never rounded (rule 10). */
export function fmtRateAsGiven(value: string): string {
  return /^-?\d+(\.\d+)?$/.test(value) ? groupWhole(value) : value;
}

/* ── THE CLOCK OF THE BOOKS ────────────────────────────────────────────────
 *
 * Operator, 2 Sept 2026: "I need Local Dubai time in my books. Globally in
 * the books." One zone, pinned, everywhere: a deal booked at 01:48 Dubai reads
 * 01:48 on a laptop in Mumbai, a phone in London and a tab left in UTC. The
 * desk's day flips at Dubai midnight — so does every "today" in the app.
 *
 * Storage is untouched: the ledger keeps UTC instants, the API sends ISO
 * strings. This is the ONE place an instant becomes a Dubai calendar, and
 * `scripts/check-books-time.mjs` fails the build on any screen that reaches
 * for the viewer's zone instead (toLocaleDateString, getHours, a UTC
 * `toISOString().slice(0,10)` day…). Month names are assembled from parts, not
 * from a locale — en-GB and en-IN both spell September "Sept", and the books
 * spell it "Sep".
 */
export const BOOKS_TZ = 'Asia/Dubai';

const CLOCK_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: BOOKS_TZ,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  weekday: 'short',
});
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

/** An instant, read on the Dubai clock. `m` is 1-based. `ymd` is the Dubai
 *  calendar day as `YYYY-MM-DD` — the day the desk would write on the ticket. */
export interface BooksClock {
  y: number; m: number; d: number;
  hh: number; mi: number; ss: number;
  wd: string;      // 'Wed'
  ymd: string;     // '2026-09-02'
  hm: string;      // '09:50'
}

export function booksClock(input: string | number | Date = Date.now()): BooksClock | null {
  const t = input instanceof Date ? input.getTime() : typeof input === 'number' ? input : Date.parse(input);
  if (!Number.isFinite(t)) return null;
  const o: Record<string, string> = {};
  for (const p of CLOCK_PARTS.formatToParts(new Date(t))) if (p.type !== 'literal') o[p.type] = p.value;
  const y = Number(o.year), m = Number(o.month), d = Number(o.day);
  const hh = Number(o.hour), mi = Number(o.minute), ss = Number(o.second);
  return {
    y, m, d, hh, mi, ss, wd: o.weekday ?? '',
    ymd: `${o.year}-${o.month}-${o.day}`,
    hm: `${o.hour}:${o.minute}`,
  };
}

/** Dubai calendar day, `YYYY-MM-DD`. Default: now. Use for reference stamps
 *  (DEAL-2026-09-02-…), file names, and any "which day is this" question. */
export function dubaiDate(input: string | number | Date = Date.now()): string {
  return booksClock(input)?.ymd ?? '';
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/* ── Wall-clock ↔ instant — the back-date door (5 Sept 2026) ───────────────
 *
 * THE CASE. Loki OTC's 3 Sept trade (62,449 USDT at 3.675 for 229,500 AED)
 * was never booked, though both money legs were recorded that day. The
 * console had no way to say WHEN a trade happened, so the operator could only
 * book it as of today — which the sweep could not settle from 3 Sept money —
 * or book it settled and double-book the money. The BFF now takes
 * `effective_at` (an ISO instant) on trades and transfers, bounded to
 * [books opened, now] and outside closed months. These helpers are the
 * console's ONLY way to build that instant.
 *
 * A <input type="datetime-local"> value carries NO ZONE. The books read it
 * as DUBAI wall-clock — on purpose, everywhere, whatever clock the operator's
 * phone keeps — the same law as every stamp on screen. Dubai is UTC+4 with
 * no daylight saving, so the offset is a constant, not a lookup. Screens make
 * the reading visible: the field says "Times are Dubai" and, once a value is
 * typed, echoes it back through the Dubai formatter with the weekday. */
export const DUBAI_UTC_OFFSET = '+04:00';

/** The first Dubai minute these books cover, in datetime-local form. Earlier
 *  history is replayed by the mirror, never booked by hand — the BFF refuses
 *  an `effective_at` before this, and the screens say so before sending. */
export const BOOKS_OPEN_WALL = '2026-09-01T00:00';

// `YYYY-MM-DDTHH:mm`, optionally `:ss` and a fraction (a browser with a
// `step` under a minute hands back seconds). The fraction is dropped: the
// books stamp to the second at most, and not every engine parses one digit.
const WALL_RE = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?(?:\.\d+)?$/;

/** Dubai wall-clock (a datetime-local value) → ISO instant, UTC. Returns ''
 *  when the value is not a wall-clock at all, so a caller can send nothing
 *  rather than something the BFF will refuse as unreadable. */
export function dubaiWallToIso(value: string): string {
  const m = WALL_RE.exec(value.trim());
  if (!m) return '';
  const wall = `${m[1]}${m[2] ?? ':00'}`;
  const t = Date.parse(`${wall}${DUBAI_UTC_OFFSET}`);
  if (!Number.isFinite(t)) return '';
  // Date.parse ROLLS a non-calendar wall-clock forward instead of refusing it
  // (review, 5 Sept 2026): '2026-09-31T10:00' became 1 October and '…T24:00'
  // became midnight of the next day, so the books would have stamped a day
  // the operator never wrote. A browser's datetime-local cannot produce those,
  // but a typed or pasted value can. Read the instant back on the Dubai clock
  // and accept it only if it says exactly what was typed.
  const c = booksClock(t);
  if (!c || `${c.ymd}T${c.hm}:${pad2(c.ss)}` !== wall) return '';
  return new Date(t).toISOString();
}

/** An ISO instant → the Dubai wall-clock in datetime-local form
 *  ('2026-09-11T10:02'), or '' when unreadable. Operator, 12 Sept 2026: a
 *  booking made from a Chain row should carry the chain's own time into
 *  "When did this happen?" — the block time is the fact, not the moment the
 *  operator got round to it. */
export function isoToDubaiWall(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const c = booksClock(t);
  return c ? `${c.ymd}T${c.hm}` : '';
}

/** The Dubai clock right now, in datetime-local form — the `max` of a
 *  back-date field. Built from booksClock, so it never reads the device zone
 *  and never slices a UTC string. */
export function dubaiWallNow(): string {
  const c = booksClock();
  return c ? `${c.ymd}T${c.hm}` : '';
}

/** Why a back-date cannot be sent, in the SAME words the BFF answers with
 *  (see lib/errors.ts) — so the operator hears it once, before the round
 *  trip, and hears one voice. null = fine, including empty, which means
 *  "now". The BFF still has the last word (closed months live there). */
export function backdateProblem(value: string): string | null {
  if (!value) return null;
  const iso = dubaiWallToIso(value);
  if (!iso) return 'That is not a time the books can read. Pick it from the field.';
  const t = Date.parse(iso);
  if (t < Date.parse(dubaiWallToIso(BOOKS_OPEN_WALL))) {
    return 'That is before 1 September, when these books began. Earlier history is replayed by the mirror, not booked here.';
  }
  if (t > Date.now()) return 'That time is in the future. Pick the time it actually happened.';
  return null;
}

/** `02 Sep, 09:50` — day and clock, Dubai. The default stamp for a booking. */
export function fmtDate(iso: string): string {
  const c = booksClock(iso);
  return c ? `${pad2(c.d)} ${MON[c.m - 1]}, ${c.hm}` : iso;
}

/** `09:50` — Dubai clock only. For rows already grouped under a day. */
export function fmtTime(iso: string | number | Date): string {
  const c = booksClock(iso);
  return c ? c.hm : String(iso);
}

/** `02 Sep` or, with year, `02 Sep 2026` — Dubai calendar day. */
export function fmtDay(iso: string | number | Date, withYear = false): string {
  const c = booksClock(iso);
  if (!c) return String(iso);
  return withYear ? `${pad2(c.d)} ${MON[c.m - 1]} ${c.y}` : `${pad2(c.d)} ${MON[c.m - 1]}`;
}

/** `September 2026` — Dubai calendar month. */
export function fmtMonth(iso: string | number | Date): string {
  const c = booksClock(iso);
  return c ? `${MONTH[c.m - 1]} ${c.y}` : String(iso);
}

/** `Wed 2 Sep 2026` — the header stamp. */
export function fmtWeekday(iso: string | number | Date = Date.now()): string {
  const c = booksClock(iso);
  return c ? `${c.wd} ${c.d} ${MON[c.m - 1]} ${c.y}` : String(iso);
}

/** Bucket a date into 'today' / 'yesterday' / 'this-week' / 'earlier' for
 *  grouping — on DUBAI calendar days. A trade at 01:48 Dubai is "today" even
 *  when the viewer's browser still says yesterday. */
export function dateBucket(iso: string): 'today' | 'yesterday' | 'this-week' | 'earlier' {
  const then = booksClock(iso);
  const now = booksClock();
  if (!then || !now) return 'earlier';
  const dayNo = (c: BooksClock) => Date.UTC(c.y, c.m - 1, c.d) / 86400000;
  const diffDays = Math.round(dayNo(now) - dayNo(then));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7)   return 'this-week';
  return 'earlier';
}

/** The compact stamp for a DENSE LOG, where nearly every row is recent:
 *  `09:50` today, `02 Sep, 09:50` any other day — yesterday too: a bare "Y" read as a typo (his "Y 11:30" on Chain and
 *  Settle later, 1 Nov 2026).
 *
 *  Shared, not copied. The trades log had this inline and the money-movement
 *  ledger needs exactly the same stamp — two copies is how one instant starts
 *  reading two ways on two screens. Dubai's clock throughout: it is assembled
 *  from `fmtTime` / `fmtDate` / `dateBucket` above, never from a second
 *  formatter. */
export function fmtStamp(iso: string): string {
  const bucket = dateBucket(iso);
  if (bucket === 'today')     return fmtTime(iso);
  return fmtDate(iso);
}

/** Relative-time stamp ("3m ago", "yesterday"). Falls back to fmtDate beyond 7d. */
export function fmtRelative(iso: string): string {
  try {
    const d = new Date(iso).getTime();
    const now = Date.now();
    const s = Math.round((now - d) / 1000);
    if (s < 60)        return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60)        return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24)        return `${h}h ago`;
    const dn = Math.round(h / 24);
    if (dn < 7)        return `${dn}d ago`;
    return fmtDate(iso);
  } catch {
    return iso;
  }
}

/**
 * The AGE of something whose date is already on screen — "3h ago", or nothing.
 *
 * `fmtRelative` falls back to `fmtDate` beyond seven days, which is right on its
 * own and wrong beside the date: a stated price from last month rendered as
 * "27 Aug, 07:18 · 27 Aug, 07:18" is not an age, it is a stutter. Callers that
 * print the instant AND how old it is use this for the second half, and print
 * nothing when the two would say the same thing.
 */
export function fmtAge(iso: string): string {
  const rel = fmtRelative(iso);
  return rel === fmtDate(iso) ? '' : rel;
}

export function shortAccount(addr: string): string {
  const parts = addr.split(':');
  if (parts.length >= 5 && parts[0] === 'treasury') {
    return `${parts[2]} · ${parts[4]}`;
  }
  if (parts.length === 3 && (parts[0] === 'customer' || parts[0] === 'counterparty')) {
    return parts[2] ?? addr;
  }
  return addr;
}

/** Tidy a party slug for table use — strips dashes/underscores.
 *  @deprecated Use `partyName()` from `./naming.ts`, which applies the full
 *  normalization law and falls back to this only when there is no name at all. */
export function tidySlug(slug: string): string {
  return slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Acronyms / city codes that should stay ALL CAPS even when they appear as a
 *  slug segment. Anything else gets Title Case via `smartCase()`.
 *
 *  Exported (additively) because `naming.ts` needs the same vocabulary to case
 *  party tokens — one table, not two. Adding a token here is safe: it only
 *  affects segments that would otherwise have been Title-Cased. */
export const ALL_CAPS = new Set([
  // City / location 3-letter codes
  'dxb', 'shj', 'auh', 'bom', 'del', 'blr', 'maa', 'hk', 'sg', 'pr',
  // Country codes seen in party names ('JP Office', 'UK desk') — same class as
  // hk/sg above, added 2026-08-12 so Title Case does not produce 'Jp Office'.
  'jp', 'uk', 'uae',
  // Asset / chain codes
  'aed', 'inr', 'usd', 'eur', 'gbp',
  'usdt', 'usdc', 'btc', 'eth',
  'trc', 'erc', 'bep', 'sol',
  // Domain shorthand
  'otc', 'kyc', 'kyt', 'aml', 'pep',
  // Internal shorthand
  'mc', 'vip', 'p2p',
  // Party-name shorthand (NAMING-AND-RELEVANCE §2.1.4) — keeps 'Lamzira AMC'
  // and 'VM-OTC' in shape instead of collapsing to 'Amc' / 'Vm-otc'.
  'vm', 'amc', 'b2b', 'tg',
]);

/** Smart-case a slug segment for display:
 *   `dxb`       → `DXB`
 *   `burjuman`  → `Burjuman`
 *   `office`    → `Office`
 *   `p-jyothi`  → `P Jyothi`
 *
 *  Primitive. It knows casing but not brands, so it renders acronym brands
 *  wrong on its own (`htx` → `Htx`). `naming.ts` layers `BRAND_MAP` over it —
 *  prefer `railName()` / `partyName()` in screens.
 */
export function smartCase(segment: string | undefined | null): string {
  if (!segment) return '';
  const s = segment.trim().toLowerCase();
  if (!s) return '';
  if (ALL_CAPS.has(s)) return s.toUpperCase();
  // mixed-character: split on dash/underscore + Title Case each token
  return s
    .replace(/[-_]+/g, ' ')
    .split(' ')
    .map((w) => (ALL_CAPS.has(w) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

/** Format a rail's `provider`/`persona` slug pair into a clean human label.
 *  Returns "Office", "DXB · Burjuman", "TRC v7", etc. */
export function railLabel(provider: string | undefined | null, persona: string | undefined | null): string {
  const p = smartCase(provider);
  const q = smartCase(persona);
  if (p && q) return `${p} · ${q}`;
  return p || q || '';
}

/** Like railLabel, but drops the provider when it duplicates the chain implied
 *  by the asset. E.g., `hot.trc.v7` USDT-TRC → "V7" (TRC redundant with asset);
 *  `hot.dxb` USDT-TRC → "DXB" (location, not chain). */
export function railLabelForAsset(
  provider: string | undefined | null,
  persona:  string | undefined | null,
  asset:    string | undefined | null,
): string {
  const chains: Record<string, string> = {
    USDTTRC: 'trc', USDTERC: 'erc', USDTBEP: 'bep',
    USDCTRC: 'trc', USDCERC: 'erc', USDCBEP: 'bep', USDCBASE: 'base',
  };
  const chainFromAsset = asset ? chains[asset] : undefined;
  const p = (provider ?? '').toLowerCase();

  // If the provider IS the chain code, drop it — the asset suffix already shows it.
  if (chainFromAsset && p === chainFromAsset) {
    return smartCase(persona) || smartCase(provider);
  }
  return railLabel(provider, persona);
}

/** Turn a raw treasury slug (`cash.dxb`, `bank.adcb.amir`, `hot.trc.v7`) into a
 *  clean human label. Drops the kind prefix and smart-cases the remainder. */
export function prettyTreasurySlug(slug: string | null | undefined): string {
  if (!slug) return '—';
  const parts = slug.split('.');
  if (parts.length < 2) return smartCase(slug);
  return parts.slice(1).map((p) => smartCase(p)).join(' · ');
}
